/**
 * openItemsAccess — geteilte Zugriffslogik für das OP-Dashboard / Offene Posten
 * (M1, Take-away-Gate Pricing-Repackaging PR-T5,
 * architecture-pricing-repackaging.md §3.3).
 *
 * Single source of truth dafür, WER die Offene-Posten-Seite als vollen Inhalt
 * (statt späterem Take-away-/Upsell-Zustand) sehen darf. Anders als Mahnwesen
 * und Vorsteuer-Cockpit hat das OP-Dashboard heute GAR KEIN Feature-Flag — es
 * ist für alle sichtbar. Deshalb gibt es hier keinen `*_FLAG`-Build-Schalter:
 * die Gate hängt ausschließlich am Master-Flag `TIER_V2_FLAG`.
 *
 * KRITISCHE Null-Delta-Invariante (OP ist LIVE + heute ungegatet): solange
 * `TIER_V2_FLAG` `false` ist, liefert `canUseOpenItems` IMMER `true` — d. h. das
 * heutige ungegatete Voll-Rendering, byte-identisch. Erst mit aktivem
 * Master-Flag (PR-T6-Aktivierung) greift die Premium-/Grandfathering-Gate:
 * Premium-Tier ODER Bestandsschutz (`grandfatheredFeatures` enthält
 * `'openItems'`) behält Zugriff.
 *
 * Firebase-frei (nur pure `entitlements`-Funktionen) — aus `OpenItemsPage.tsx`
 * und `config/navigation.ts` importierbar.
 *
 * Im **Advisor-Kontext** (Steuerberater im Mandanten-Tenant) trägt das Gate
 * KEINEN Owner-Tier-Term: ein StB darf NIE durch den fehlenden Premium-Status
 * seines eigenen (Kanzlei-)Kontos aus den Mandanten-Flächen ausgesperrt werden
 * (M-A1 / architecture-grace-window.md §6.3, T4b-Muster wie
 * `datevExportAccess.ts`). Deshalb der explizite `isAdvisorContext`-Parameter.
 *
 * @module config/openItemsAccess
 */

import {
  resolveTier,
  hasMinTier,
  isGrandfathered,
  TIER_V2_FLAG,
  type SellerLike,
} from './entitlements';

/**
 * Entscheidet fail-closed, ob ein Nutzer den vollen OP-Dashboard-Inhalt sehen
 * darf.
 *
 * Reihenfolge (Take-away-Gate §3.3, Advisor-Bypass M-A1 §6.3):
 *   1. Advisor-Kontext ⇒ `true` (KEIN Owner-Tier-Term — ein StB wird nie durch
 *      den Tier seines eigenen Kontos aus Mandanten-Flächen gesperrt, §6.3).
 *   2. Master-Flag aus (`!TIER_V2_FLAG`) → immer (IST-Verhalten, Null-Delta —
 *      byte-identisch zum heutigen ungegateten OP-Dashboard).
 *   3. Sonst: Premium-Tier ODER Grandfathering für `'openItems'`.
 *
 * @param sellerData - Roh-Seller-Doc-Daten aus `useAuth().sellerData` (oder
 *   `null`, solange nicht geladen). Fail-closed bei `null`/Nicht-Objekt.
 * @param isAdvisorContext - `true`, wenn der aktuelle Nutzer ein Steuerberater im
 *   Mandanten-Kontext ist (`!isOwnScope` / readOnly, §6.3) — NICHT
 *   `useIsAdvisorContext()` (eigener Kanzlei-Scope = Owner-Pfad). Dann KEIN
 *   Owner-Tier-Term, das OP-Dashboard bleibt offen.
 * @returns `true`, wenn der Nutzer das OP-Dashboard voll nutzen darf.
 */
export function canUseOpenItems(
  sellerData: SellerLike | null | undefined,
  isAdvisorContext: boolean,
): boolean {
  // §6.3 Advisor-Kontext: kein Owner-Tier-Term, StB nie aussperren.
  if (isAdvisorContext) {
    return true;
  }
  if (!TIER_V2_FLAG) {
    return true;
  }
  return (
    hasMinTier(resolveTier(sellerData), 'premium') ||
    isGrandfathered(sellerData, 'openItems')
  );
}
